import { useMergeRefs } from '@floating-ui/react'
import { css, cx } from '@linaria/core'
import { type FC, type HTMLProps, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import CloseIcon from '#icons/close-24.svg'
import SearchIcon from '#icons/search-24.svg'
import { fragments, style } from '#styles/index'
import { sharedTestIds } from '#utils/sharedTestIds'
import { IconButton } from './IconButton'

const filterClass = css`
  z-index: 1;
  position: sticky;
  height: 47.5px;
  width: 100%;
  padding-right: 2px;
  border-bottom: 1px solid ${style.theme.borderSurface};
  background-color: ${style.theme.backgroundSurfacePrimary};
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;

  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 5px;

  // children fill the entire container
  > *, &::before {
    grid-column: 1;
    grid-row: 1;
    height: 100%;
    width: 100%;
  }

  &:hover > .search {
    outline-color: ${style.theme.borderDanger};
  }

  // ::before (the search icon) is just there to position the image where it is supposed to be sitting
  &::before {
    content: " ";
    z-index: -1;
    mask: url(${SearchIcon}) no-repeat 20px center /20px 20px;
    background: ${style.theme.textDefault};
    transition: background-color 0.25s ease;
  }

  > .search {
    border: none;
    padding: 12px 38px 12px 45px;
    background-color: transparent;
    caret-color: ${style.theme.textDefault};
    color: ${style.theme.textDefault};
    ${fragments.textStyle.body.m.regular}

    outline-offset: -4px;
    outline: 2px solid transparent;
    transition: 0.25s ease outline-color;

    &:focus {
      outline-offset: -4px;
      outline: 2px solid ${style.theme.borderOutline};
    }

    &::placeholder {
      ${fragments.textStyle.body.m.regular};
      color: ${style.theme.textSoft};
    }
  }

  // animation class gets added when the search shortcut has been called
  &._with-animation > .search {
    animation: 1.0s linear 1 both reveal-search;
    // search animation flickers a bit to call for attention
    @keyframes reveal-search {
      0% { outline-color: transparent;}
      25% { outline-color: ${style.theme.borderDanger}; }
      50% { outline-color: ${style.theme.borderOutline}; }
      75% { outline-color: ${style.theme.borderDanger}; }
      100% { outline-color: ${style.theme.borderOutline}; }
    }
  }

  > .close {
    width: min-content;
    justify-self: end;
    // so it doesn't grab the pointer focus
    margin-right: 16px;
    color: ${style.theme.textDefault};
    opacity: 0;
    transition: opacity 0.25s ease;
    height: auto;
  }

  &:focus-within, &._with-input {

    // the search icon changes color when there's input or the search box has been clicked
    &::before {
      background: ${style.theme.textAccent};
    }
    // the close button appears
    > .close {
      opacity: 1;
    }
  }
`

export const Search: FC<{ onClear?: React.MouseEventHandler<HTMLButtonElement>; focusAnimation?: boolean } & HTMLProps<HTMLInputElement>> = ({
  ref: outerRef,
  onClear,
  value,
  focusAnimation,
  className,
  ...props
}) => {
  const innerRef = useRef<HTMLInputElement>(null)
  const combinedRef = useMergeRefs([outerRef, innerRef])
  const { t } = useTranslation()

  return (
    <div className={cx('search-area', filterClass, focusAnimation && '_with-animation', value && '_with-input', className)}>
      <input
        value={value}
        ref={combinedRef}
        className="search"
        aria-label={t('shared:common.label.search')}
        placeholder={t('shared:common.input.placeholder-search')}
        {...props}
      />
      <IconButton
        className="close"
        iconUrl={CloseIcon}
        title={t('shared:common.button.clear-search')}
        onClick={(e) => {
          onClear?.(e)
        }}
        data-testid={sharedTestIds.filter.sidebarSearchClearButton}
      />
    </div>
  )
}
